import { FC, useState, useMemo, useEffect } from 'react';
import { Grid, Button, Box, Typography, CircularProgress } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PaginatedGrid from '../paginatedGrid/PaginatedGrid';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { AttendanceComponentProps } from './attendanceComponent.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TPrimaryDetails } from '../../../apis/users/users.interfaces';
import {
  TAttendanceByDateRecord,
  TAttendanceByUserResponse,
} from '../../../apis/__shared/attendance.interfaces';
// import DateBasedAttendanceDetailsComponent from './dateBasedAttendanceDetailsComponent';
import UserBasedAttendanceComponent from './userBasedAttendanceComponent';
import { EditButton } from 'src/views/components/simple/AllButtons';
import { useResponsive } from 'src/hooks/useResponsiveHook';

enum EAttendanceView {
  GRID = 'grid',
  DETAILS = 'details',
  USER_VIEW = 'userView',
}

const DateBasedAttendanceComponent: FC<AttendanceComponentProps> = ({
  atendanceRecords,
  onEditAttendanceClickHandler,
  onViewChange,
  onFetchUserAttendance,
}) => {
  const { translateText } = useTranslation();
  const { isMobile } = useResponsive();
  const [viewType, setViewType] = useState<EAttendanceView>(
    EAttendanceView.GRID
  );
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] =
    useState<TAttendanceByDateRecord | null>(null);
  const [selectedUserAttendanceRecords, setSelectedUserAttendanceRecords] =
    useState<Array<{ date: string; record: TAttendanceByDateRecord }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [userAttendanceData, setUserAttendanceData] =
    useState<TAttendanceByUserResponse | undefined>();
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);

  // Normalize records to array format
  const recordsArray = useMemo(() => {
    if (isEmpty(atendanceRecords)) return [];
    return Array.isArray(atendanceRecords)
      ? atendanceRecords
      : Object.entries(atendanceRecords).map(([date, records]) => ({
        date,
        records,
      }));
  }, [atendanceRecords]);

  // Find all attendance records for a user across all dates
  const findAllAttendanceRecordsForUser = (
    userId: string
  ): Array<{ date: string; record: TAttendanceByDateRecord }> => {
    const userRecords: Array<{
      date: string;
      record: TAttendanceByDateRecord;
    }> = [];
    recordsArray.forEach(({ date, records }) => {
      const record = records.find(
        (r: TAttendanceByDateRecord) => r.userDetails?.id === userId
      );
      if (record) {
        userRecords.push({ date, record });
      }
    });
    return userRecords;
  };

  const handleRowClick = async (rowData: any) => {
    const userId = rowData.id;

    // If fetch function is provided, fetch user-based attendance and show user view
    if (onFetchUserAttendance && userId) {
      setIsLoadingUserData(true);
      setSelectedUserId(userId);
      try {
        const userData = await onFetchUserAttendance(userId);
        setUserAttendanceData(userData);
        setViewType(EAttendanceView.USER_VIEW);
        onViewChange?.(true);
      } catch (error) {
        console.error('Error fetching user attendance:', error);
        // Fallback to details view if fetch fails
        const allRecords = findAllAttendanceRecordsForUser(userId);
        if (allRecords.length > 0) {
          setSelectedUserAttendanceRecords(allRecords);
          setSelectedAttendanceRecord(allRecords[0].record);
          setViewType(EAttendanceView.DETAILS);
          onViewChange?.(true);
        }
      } finally {
        setIsLoadingUserData(false);
      }
    } else {
      // Fallback to details view if no fetch function
      const allRecords = findAllAttendanceRecordsForUser(userId);
      if (allRecords.length > 0) {
        setSelectedUserAttendanceRecords(allRecords);
        setSelectedAttendanceRecord(allRecords[0].record);
        setViewType(EAttendanceView.DETAILS);
        onViewChange?.(true);
      }
    }
  };

  const handleBackToGrid = () => {
    setViewType(EAttendanceView.GRID);
    setSelectedAttendanceRecord(null);
    setSelectedUserId(undefined);
    setUserAttendanceData(undefined);
    onViewChange?.(false);
  };

  const handleEditClick = () => {
    if (selectedAttendanceRecord) {
      onEditAttendanceClickHandler(selectedAttendanceRecord);
    }
  };

  useEffect(() => {
    onViewChange?.(viewType === EAttendanceView.DETAILS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  const getDisplayName = (userDetails: TPrimaryDetails) => {
    return [userDetails.firstName, userDetails.middleName, userDetails.lastName]
      .filter(Boolean)
      .join(' ');
  };

  const getAttendanceGridRowsToRender = () => {
    if (isEmpty(recordsArray)) return [];

    // Extract unique users from attendance records
    const userMap = new Map<string, TPrimaryDetails>();
    recordsArray.forEach(({ records }) => {
      records.forEach((record: TAttendanceByDateRecord) => {
        const userId = record.userDetails.id;
        if (userId && !userMap.has(userId)) {
          userMap.set(userId, record.userDetails);
        }
      });
    });

    // Build rows directly from TAttendanceByDateResponse structure
    const rows: Array<{
      id: string;
      basicDetails: TPrimaryDetails;
      attendance: { [date: string]: string };
    }> = [];

    userMap.forEach((userDetails, userId) => {
      const attendanceMap: { [date: string]: string } = {};

      recordsArray.forEach(({ date, records }) => {
        const attendanceRecord = records.find(
          (x: TAttendanceByDateRecord) => x.userDetails?.id === userId
        );
        attendanceMap[date] = attendanceRecord?.attendanceStatus ?? '';
      });

      rows.push({
        id: userId,
        basicDetails: userDetails,
        attendance: attendanceMap,
      });
    });
    return rows;
  };

  const getDateSpecificColumns = (): GridColDef[] => {
    return recordsArray.map(({ date }) => {
      return {
        field: date,
        headerName: date,
        width: 120,
        renderCell: (params) => {
          const row = params.row as { attendance: { [date: string]: string } };
          return `${row?.attendance[date] ? translateText('attendance.yes') : translateText('attendance.no')}`;
        },
      };
    });
  };

  const getColumnsForGrid = () => {
    const columns: GridColDef[] = [
      {
        field: 'basicDetails.firstName',
        headerName: translateText('attendance.name'),
        width: 160,
        renderCell: (params) => {
          const row = params.row as { basicDetails: TPrimaryDetails };
          return `${row.basicDetails.firstName ? row.basicDetails.firstName + ' ' : ''
            }${row.basicDetails.middleName || ''}${row.basicDetails.lastName ? ' ' + row.basicDetails.lastName : ''
            }`.trim();
        },
      },
      ...getDateSpecificColumns(),
      // {
      //   field: 'actions',
      //   headerName: translateText('attendance.actions'),
      //   width: 120,
      //   renderCell: (params) => {
      //     return (
      //       <RowActions record={params} onRowClickHandler={handleRowClick} />
      //     );
      //   },
      // },
    ];
    return columns;
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{
        height: '100%',
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 2 },
      }}
      data-testid="date-based-attendance-component"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
        }}
      >
        {(viewType === EAttendanceView.DETAILS && selectedAttendanceRecord) ||
          viewType === EAttendanceView.USER_VIEW ? (
          <Box
            display="flex"
            alignItems="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
            width={{ xs: '100%', sm: 'auto' }}
            data-testid="attendance-detail-header"
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToGrid}
              sx={{
                mr: { xs: 0, sm: 2 },
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="back-to-grid-button"
            >
              {translateText('attendance.backToGrid')}
            </Button>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: { xs: 500, sm: 600 },
                textAlign: { xs: 'center', sm: 'left' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="attendance-detail-header-title"
            >
              {viewType === EAttendanceView.USER_VIEW
                ? translateText('attendance.userView')
                : selectedAttendanceRecord
                  ? getDisplayName(selectedAttendanceRecord.userDetails)
                  : translateText('attendance.details.title')}
            </Typography>
          </Box>
        ) : (
          <Box
            data-testid="attendance-primary-header"
            width={{ xs: '100%', sm: 'auto' }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
              data-testid="attendance-grid-title"
            >
              {translateText('attendance.title')}
            </Typography>
          </Box>
        )}

        {viewType === EAttendanceView.DETAILS && selectedAttendanceRecord && (
          <Box width={{ xs: '100%', sm: 'auto' }}>
            <EditButton
              label={translateText('attendance.edit')}
              onClickHandler={handleEditClick}
              data-testid="edit-attendance-button"
            />
          </Box>
        )}
      </Grid>

      {viewType === EAttendanceView.USER_VIEW &&
        selectedUserId &&
        userAttendanceData ? (
        <Grid item xs={12} data-testid="user-attendance-container">
          <UserBasedAttendanceComponent
            atendanceRecords={userAttendanceData}
            onEditAttendanceClickHandler={onEditAttendanceClickHandler}
            onFetchUserAttendance={onFetchUserAttendance}
            userId={selectedUserId}
            onBackToDateView={handleBackToGrid}
            hideBackButton={false}
          />
        </Grid>
      ) : viewType === EAttendanceView.DETAILS &&
        selectedAttendanceRecord &&
        selectedUserAttendanceRecords.length > 0 ? (
        <Grid item xs={12} data-testid="attendance-details-container">
          <Typography variant="body1" data-testid="attendance-details-fallback">
            {translateText('attendance.details.title')}
          </Typography>
          {/* DateBasedAttendanceDetailsComponent would render here */}
        </Grid>
      ) : isLoadingUserData ? (
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: { xs: '300px', sm: '400px' },
          }}
          data-testid="loading-user-attendance"
        >
          <CircularProgress />
        </Grid>
      ) : (
        <Grid
          item
          xs={12}
          data-testid="attendance-grid-container"
          sx={{
            maxHeight: {
              xs: 'calc(100vh - 200px)',
              sm: 'calc(100vh - 250px)',
              md: 'calc(100vh - 300px)'
            },
            height: 'fit-content',
            overflow: 'auto',
          }}
        >
          <PaginatedGrid
            rows={getAttendanceGridRowsToRender()}
            columns={getColumnsForGrid()}
            dataTestId="attendance-grid"
            onRowClick={handleRowClick}
            hideColumnsOnMobile={isMobile && getDateSpecificColumns().length > 3
              ? getDateSpecificColumns().slice(3).map(col => col.field)
              : []}
            mobilePageSize={5}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default DateBasedAttendanceComponent;
